import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import axios from "axios";

import { readCookie } from "@/shared/lib/cookies";
import { logger } from "@/shared/lib/logger";
import type { LogLevel } from "@/shared/lib/logger";

import type { AppError, AppErrorKind } from "./errors";
import { normalizeError } from "./errors";

/**
 * Розширення axios-конфігу прапором, який несуть запити, що САМІ обробляють 401
 * (login-сабміт, bootstrap) — щоб глобальний 401-handler (ітер.2, ADR-0006 D4)
 * їх не перехоплював і не зациклював редірект. У ітер.1 прапор лише декларується
 * (response-interceptor зʼявиться в ітер.2).
 */
declare module "axios" {
  // Параметри типу мусять збігатися з оригіналом axios (`<D = any>`), інакше TS2428.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- дзеркалимо сигнатуру axios для module augmentation
  export interface AxiosRequestConfig<D = any> {
    skipAuthRedirect?: boolean;
  }
}

/** Методи, що змінюють стан → несуть CSRF double-submit (ADR-0002 D4 / ADR-0006 D3). */
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * CSRF request-interceptor (ADR-0006 D3): на мутаційних методах ехоїть
 * non-HttpOnly cookie `csrf` у заголовок `X-CSRF-Token`. GET/HEAD — не чіпаємо.
 * Cookie немає (login публічний, ще без сесії) → заголовок не ставимо.
 * Self-contained у `shared` (читає лише cookie) — FSD не порушено.
 */
export function csrfRequestInterceptor(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const method = (config.method ?? "get").toUpperCase();
  if (MUTATING_METHODS.has(method)) {
    const token = readCookie("csrf");
    if (token) {
      config.headers.set("X-CSRF-Token", token);
    }
  }
  return config;
}

/** Навішує CSRF request-interceptor на переданий axios-клієнт. */
export function installCsrfInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use(csrfRequestInterceptor);
}

/**
 * 401-seam (ADR-0006 D4, узагальнено ADR-0010 D4). Конкретний handler
 * (`session.reset()` + редірект на `/login?return=`) реєструє `app/providers` —
 * `shared` НЕ імпортує `entities`/`app`, лише викликає зареєстровану функцію.
 * До реєстрації — `null` (no-op): seam дрімає до feature-007 ітер.2.
 */
type UnauthorizedHandler = (err: AppError) => void;

let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn;
}

/** Поточний роут = `/login` (history-mode: pathname == route path). Анти-зацикл. */
function isOnLoginRoute(): boolean {
  return window.location.pathname === "/login";
}

/**
 * `kind` → рівень логу (ADR-0011 D6, рішення власника № 2): рутинні 4xx → `debug`
 * (у prod нижче порогу, не шумлять); `server`/`unknown` → `error`; `network` → `warn`.
 */
function levelFor(kind: AppErrorKind): LogLevel {
  switch (kind) {
    case "server":
    case "unknown":
      return "error";
    case "network":
      return "warn";
    default:
      return "debug";
  }
}

/** pathname без query (§7): query може нести `return=`/токени. */
function pathnameOf(url?: string): string | undefined {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url, "http://_").pathname;
  } catch {
    return undefined;
  }
}

/**
 * Лог HTTP-помилки через logger-порт (ADR-0011 D4). Рівень за `kind`-мапою;
 * `method`/`path`(без query)/`requestId`(звʼязок із BE-логом) у context. PII не
 * передаємо — лише похідні поля; `sanitize` у dispatcher — друга лінія захисту.
 */
function logHttpError(rawErr: unknown, appError: AppError): void {
  const config = axios.isAxiosError(rawErr) ? rawErr.config : undefined;
  const response = axios.isAxiosError(rawErr) ? rawErr.response : undefined;
  const requestId = response?.headers?.["x-request-id"] as string | undefined;

  logger[levelFor(appError.kind)]("http-error", {
    source: "http",
    kind: appError.kind,
    status: appError.status,
    code: appError.code,
    method: config?.method?.toUpperCase(),
    path: pathnameOf(config?.url),
    requestId,
  });
}

/**
 * Глобальний normalize-interceptor (ADR-0010 D4). На rejection-шляху:
 *
 * 1. `normalizeError(rawErr)` — **завжди**, будь-який reject → `AppError`;
 * 2. лог події спостереження (ADR-0011 D4): `logHttpError` через logger-порт,
 *    окрім `axios.isCancel` (скасований запит не логуємо). Це **той самий** і
 *    **єдиний** interceptor — без другого дубля;
 * 3. єдиний cross-cutting side-effect — 401-редірект: якщо `kind==='auth'`
 *    **і** не `skipAuthRedirect` **і** не на `/login` → `onUnauthorized(appError)`;
 * 4. `Promise.reject(appError)` — споживачі завжди `catch (e: AppError)`.
 *
 * `skipAuthRedirect` гасить ЛИШЕ крок 2 (редірект) — нормалізація (1) і reject (3)
 * відбуваються завжди (login-сабміт/bootstrap дістають `AppError` без редіректу).
 * Interceptor канал-агностичний (не авто-тостить/не авто-алертить) — канал на call-site.
 */
export function installAuthResponseInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    (rawErr: unknown) => {
      const appError = normalizeError(rawErr);

      // Скасований запит (AbortController → CanceledError) — не подія спостереження.
      if (!axios.isCancel(rawErr)) {
        logHttpError(rawErr, appError);
      }

      const skipRedirect
        = axios.isAxiosError(rawErr) && rawErr.config?.skipAuthRedirect === true;

      if (appError.kind === "auth" && !skipRedirect && !isOnLoginRoute()) {
        onUnauthorized?.(appError);
      }

      return Promise.reject(appError);
    },
  );
}
